// @flow
// A record's text and the rich-text editor's document, both ways, with nothing of the editor itself (this file is in
// the app's bundle; the editor loads on demand).
//
// A record's text comes in two kinds:
// - plain text (isPlainRecord): what the app's textarea saves (lines ending in <br>, a "<" or a "&" as typed) and the
//   External API's text (lines ending in \n), when the page shows it as typed: no tag but the line break. The editor
//   reads it as the textarea does: its lines, and only the entities the textarea decodes (&nbsp; &amp; &quot; &lt; &gt;).
// - HTML: what the editor saves, the migrations' <b> and whole pages, and a typed "<" the page reads as a tag
//   ("Glicemia <normal": the timeline hides "<normal"). The editor reads it as the timeline shows it
//   (sanitizeRecordFragment): the same nodes, read as lines (the record shows in a `white-space: pre-wrap` box).
//
// The editor's document (ProseMirror JSON) has only paragraphs, bullet and numbered lists of paragraphs, hard breaks,
// and three marks: bold, italic, highlight. It is saved in the record's own flat format, so every app version shows it:
// - a paragraph is a line: paragraphs are joined by <br> (an empty paragraph is an empty line), as the textarea saves;
//   a hard break (Shift+Enter, a pasted <br>) ends a line too, and is saved as one: the marks open on it close before
//   the <br> and open again after it (<strong>Queixa:</strong><br><strong>febre</strong>), the form it is read back in;
// - bold <strong>, italic <em>, highlight <mark>; lists <ul>/<ol> of <li>, which make their own lines;
// - no newline and no space between tags: the record is shown with pre-wrap, where those would be lines too;
// - text escaped (& < > and the no-break space as &nbsp;).
//
// The editor opens only a record it gives back as it is (readRecord's `editable`): plain text, whose entities the page
// reads as the textarea does, and HTML already in the editor's own format (the record, read and saved again, is the
// same string to the character). Anything else (<p> lines, <b>, a list written with newlines, a link, a style…) stays
// in the plain textarea, as it always did, and the editor never rewrites it.
import { sanitizeRecordFragment, getInertDocument } from '@helpers/helpers_index/sanitizeHtml';

type Mark = { type: string };
type TextNode = { type: 'text', text: string, marks?: Array<Mark> };
type InlineNode = TextNode | { type: 'hardBreak', marks?: Array<Mark> };
type BlockNode = {
  type: string, // paragraph, bulletList, orderedList, listItem
  content?: Array<any>,
};
export type EditorDoc = { type: 'doc', content: Array<BlockNode> };

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

// The marks in the editor's order (its schema's): the outer tag first when they nest
export const MARKS = ['bold', 'italic', 'highlight'];
const MARK_OF_TAG = {
  b: 'bold', strong: 'bold', i: 'italic', em: 'italic', mark: 'highlight',
};
// Each mark's tag, here and in the field's picture of the record while the editor loads (RecordTextField)
export const TAG_OF_MARK = { bold: 'strong', italic: 'em', highlight: 'mark' };
const HEADINGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const LISTS = { ul: 'bulletList', ol: 'orderedList' };
// What the sanitizer keeps that starts a line of its own (everything else it keeps is inline)
const BLOCKS = new Set([
  'p', 'div', 'blockquote', 'pre', 'hr', 'address', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'dl', 'dt',
  'dd', 'details', 'summary', 'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]);

const sortMarks = (marks: Array<string>): Array<string> => MARKS.filter((mark) => marks.includes(mark));

// A paragraph of the editor's document (an empty one has no content), here and for a paste (pastedText.js)
export const paragraph = (inline: Array<any>): BlockNode => (inline.length ? { type: 'paragraph', content: inline } : { type: 'paragraph' });

// ---------------------------------------------------------------------------------------------------------------------
// Plain text or HTML

// A "<" the page starts a tag or a comment with (the HTML tokenizer): before a letter, a "/", a "!" or a "?"
const TAG_START = /<[a-z/!?]/gi;
const LINE_BREAK = /^<br\s*\/?>/i;

// Whether the record is plain text (the textarea's, the External API's): its only tags are line breaks (<br>, <br/>,
// <br />, any case), so the timeline shows it as typed. Any other "<" before a letter, a "/", a "!" or a "?" starts a
// tag for the page, which hides what follows it ("Glicemia <normal", "x<y", "</ b"): read as HTML, the record opens in
// the editor only if the editor gives back the same text (readRecord), so it keeps opening in the textarea. A "<"
// before a number, a space or a sign is text ("<140x90", "< 120", "<3 meses", "<="). Cheap: most records are plain,
// and are never parsed.
export const isPlainRecord = (content: ?string): boolean => {
  const text = String(content || '');
  if (!text.includes('<')) return true;
  TAG_START.lastIndex = 0;
  for (let match = TAG_START.exec(text); match; match = TAG_START.exec(text)) {
    if (!LINE_BREAK.test(text.slice(match.index, match.index + 64))) return false;
  }
  return true;
};

// The entities the textarea decodes (replaceHtmlEntitesToText), the no-break space kept as one so it comes back
const TEXTAREA_ENTITIES = {
  nbsp: '\u00a0', amp: '&', quot: '"', lt: '<', gt: '>',
};
const TEXTAREA_ENTITY = /&(nbsp|amp|quot|lt|gt);/g;
const decodeAsTextarea = (text: string): string => text.replace(TEXTAREA_ENTITY, (match, name) => TEXTAREA_ENTITIES[name]);
const plainLines = (text: string): Array<string> => text.replace(/\r\n?/g, '\n').split(/<br\s*\/?>|\n/i);

// A text without "<", as an HTML parser reads it (its entities, &eacute; or &#39;), in the sanitizer's document with
// no browsing context: with no tag in it, it is only text
const decodeHtmlText = (text: string): string => {
  if (!text.includes('&')) return text.replace(/\r\n?/g, '\n');
  const holder = getInertDocument().createElement('div');
  holder.innerHTML = text.replace(/</g, '&lt;');
  return holder.textContent;
};

// Whether the page (an HTML parser) reads the plain record's entities as the textarea does. It may not: "&eacute;",
// "&#39;" or "&para" show as a character in the timeline and as typed in the textarea; saved from the editor, they
// would become text ("&amp;eacute;") and the timeline would change.
const entitiesReadAlike = (text: string): boolean => {
  if (!text.replace(TEXTAREA_ENTITY, '').includes('&')) return true;
  return plainLines(text).every((line) => decodeAsTextarea(line) === decodeHtmlText(line));
};

// ---------------------------------------------------------------------------------------------------------------------
// Record → document
//
// Plain text: one paragraph per line. HTML: the text between blocks is read as lines: a <br> or a \n ends one. The
// browser doesn't show the empty line a <br> leaves at the end of a block, or right before one ("Queixa<br><ul>" is
// the line "Queixa", then the list), so neither does the editor; at the very end of the record it does keep it (the
// textarea did, and saving it gives the same text back).

const textToDoc = (text: string): EditorDoc => ({
  type: 'doc',
  content: plainLines(text).map((line) => paragraph(line ? [{ type: 'text', text: decodeAsTextarea(line) }] : [])),
});

type Run = { lines: Array<Array<InlineNode>>, broken: boolean, used: boolean };
const newRun = (): Run => ({ lines: [[]], broken: false, used: false });

const addText = (run: Run, text: string, marks: Array<string>) => {
  const parts = text.replace(/\r/g, '').split('\n');
  parts.forEach((part, index) => {
    if (index > 0) {
      run.lines.push([]);
      run.broken = true;
    }
    run.used = true;
    if (!part) return;
    const line = run.lines[run.lines.length - 1];
    const sorted = sortMarks(marks);
    const last = line[line.length - 1];
    const sameMarks = last && last.type === 'text'
      && ((last.marks || []).map((mark) => mark.type).join() === sorted.join());
    if (sameMarks && last && last.type === 'text') {
      last.text += part;
    } else {
      line.push(sorted.length ? { type: 'text', text: part, marks: sorted.map((type) => ({ type })) } : { type: 'text', text: part });
    }
  });
};

// The run's lines as paragraphs, into `out`. `last`: nothing follows the run in the whole record.
const flush = (run: Run, out: Array<BlockNode>, last: boolean) => {
  if (!run.used) return;
  const { lines } = run;
  if (!last && run.broken && lines[lines.length - 1].length === 0) lines.pop();
  lines.forEach((line) => out.push(paragraph(line)));
};

type Walk = { run: Run, out: Array<BlockNode> };

// The children of `parent` into walk.out; `root`: parent is the whole record (the sanitizer's nodes)
const walkChildren = (parent: any, walk: Walk, marks: Array<string>, root: boolean) => {
  const children = parent.childNodes;
  for (let i = 0; i < children.length; i += 1) {
    // eslint-disable-next-line no-use-before-define
    walkNode(children[i], walk, marks);
  }
  // The end of a block: what it leaves open ends with it
  flush(walk.run, walk.out, root);
  walk.run = newRun(); // eslint-disable-line no-param-reassign
};

const listOf = (list: any, marks: Array<string>): BlockNode => {
  const items = [];
  const children = list.childNodes;
  for (let i = 0; i < children.length; i += 1) {
    const child = children[i];
    // The spaces and lines between the items aren't text of the list
    const isText = child.nodeType === TEXT_NODE;
    if (!isText || /\S/.test(child.data)) {
      const content = [];
      const holder = child.nodeType === ELEMENT_NODE && child.localName === 'li' ? child : { childNodes: [child] };
      walkChildren(holder, { run: newRun(), out: content }, marks, false);
      // An item starts with a paragraph (its first line), even when it holds only a list
      if (!content.length || content[0].type !== 'paragraph') content.unshift({ type: 'paragraph' });
      items.push({ type: 'listItem', content });
    }
  }
  return { type: LISTS[list.localName], content: items };
};

const walkNode = (node: any, walk: Walk, marks: Array<string>) => {
  if (node.nodeType === TEXT_NODE) {
    addText(walk.run, node.data, marks);
    return;
  }
  if (node.nodeType !== ELEMENT_NODE) return;
  const tag = node.localName;
  if (tag === 'br') {
    walk.run.lines.push([]);
    walk.run.broken = true; // eslint-disable-line no-param-reassign
    walk.run.used = true; // eslint-disable-line no-param-reassign
    return;
  }
  if (!BLOCKS.has(tag)) {
    const mark = MARK_OF_TAG[tag];
    const inside = mark && !marks.includes(mark) ? [...marks, mark] : marks;
    for (let i = 0; i < node.childNodes.length; i += 1) walkNode(node.childNodes[i], walk, inside);
    return;
  }
  // A block: the lines before it end here, and it makes its own
  flush(walk.run, walk.out, false);
  walk.run = newRun(); // eslint-disable-line no-param-reassign
  if (LISTS[tag]) {
    const list = listOf(node, marks);
    if (list.content && list.content.length) walk.out.push(list);
    return;
  }
  // A heading is a bold line
  const inside = HEADINGS.has(tag) && !marks.includes('bold') ? [...marks, 'bold'] : marks;
  walkChildren(node, walk, inside, false);
};

const treeToDoc = (root: any): EditorDoc => {
  const out = [];
  walkChildren(root, { run: newRun(), out }, [], true);
  return { type: 'doc', content: out.length ? out : [{ type: 'paragraph' }] };
};

// The record's text (content or content_draft) as the editor's document: plain text as the textarea reads it, HTML
// as the timeline shows it
export const recordToDoc = (content: ?string): EditorDoc => {
  const text = String(content || '');
  return isPlainRecord(text) ? textToDoc(text) : treeToDoc(sanitizeRecordFragment(text));
};

// ---------------------------------------------------------------------------------------------------------------------
// Document → record

const escapeText = (text: string): string => text
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/\u00a0/g, '&nbsp;');

// A line's text, its marks opened and closed within it
const inlineHtml = (nodes: Array<any> = []): string => {
  let html = '';
  let open: Array<string> = [];
  const closeTo = (depth: number) => {
    while (open.length > depth) html += `</${TAG_OF_MARK[open.pop()]}>`;
  };
  nodes.forEach((node) => {
    const marks = sortMarks((node.marks || []).map((mark) => mark.type));
    if (node.type !== 'text' || !node.text) return;
    let keep = 0;
    while (keep < open.length && keep < marks.length && open[keep] === marks[keep]) keep += 1;
    closeTo(keep);
    marks.slice(keep).forEach((mark) => { html += `<${TAG_OF_MARK[mark]}>`; });
    open = marks;
    html += escapeText(node.text);
  });
  closeTo(0);
  return html;
};

// A paragraph's lines: a hard break ends one, as a line break in its text would. Each line is written with its own
// marks (closed before the <br>, opened again after it): read back, the record is the same lines with the same marks,
// and saved again the same string, wherever the break was (in bold, at a line's end, right before a list).
const linesOf = (inline: Array<any> = []): Array<Array<TextNode>> => {
  const lines = [[]];
  inline.forEach((node) => {
    if (node.type === 'hardBreak') {
      lines.push([]);
      return;
    }
    if (node.type !== 'text' || !node.text) return;
    node.text.replace(/\r/g, '').split('\n').forEach((part, index) => {
      if (index > 0) lines.push([]);
      if (part) lines[lines.length - 1].push({ ...node, text: part });
    });
  });
  return lines;
};

// Blocks as the record's text. `root`: the whole record (its end keeps an empty last line as it is).
const blocksHtml = (blocks: Array<BlockNode> = [], root: boolean): string => {
  let html = '';
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.type === 'paragraph') {
      const lines = [];
      while (i < blocks.length && blocks[i].type === 'paragraph') {
        lines.push(...linesOf(blocks[i].content));
        i += 1;
      }
      html += lines.map((line) => inlineHtml(line)).join('<br>');
      // An empty last line before a list or at the end of an item needs a <br> of its own to show (and to come back)
      const atEnd = i >= blocks.length;
      if ((!atEnd || !root) && lines[lines.length - 1].length === 0) html += '<br>';
    } else {
      const tag = block.type === 'orderedList' ? 'ol' : 'ul';
      const items = (block.content || []).map((item) => `<li>${blocksHtml(item.content, false)}</li>`).join('');
      html += `<${tag}>${items}</${tag}>`;
      i += 1;
    }
  }
  return html;
};

export const docToRecord = (doc: ?EditorDoc): string => (doc ? blocksHtml(doc.content, true) : '');

// ---------------------------------------------------------------------------------------------------------------------
// Opening a record

const hasFormattingIn = (blocks: Array<any> = []): boolean => blocks.some((block) => (
  block.type === 'bulletList' || block.type === 'orderedList'
  || (block.content || []).some((node) => (node.marks && node.marks.length) || (node.content && hasFormattingIn([node])))
));

export type RecordReading = {
  doc: EditorDoc, // what the editor opens
  editable: boolean, // the editor gives the record back as it is: it may open it (otherwise the textarea, as always)
  formatted: boolean, // it shows formatting the editor makes (bold, italic, highlight, lists), which the textarea can't
};

// The record as the field opens it, read once
export const readRecord = (content: ?string): RecordReading => {
  const text = String(content || '');
  if (isPlainRecord(text)) return { doc: textToDoc(text), editable: entitiesReadAlike(text), formatted: false };
  const doc = treeToDoc(sanitizeRecordFragment(text));
  // Only what the editor itself writes: saved again, the very same string
  const editable = docToRecord(doc) === text;
  return { doc, editable, formatted: editable && hasFormattingIn(doc.content) };
};
